import { useState, useEffect } from 'react';
import { getTeamsSsoToken } from '../teams/useTeamsContext';
import { apiClient } from '../api/client';

interface User {
  microsoftUserId: string;
  microsoftUpn: string;
  displayName: string;
  boondUserId?: string;
  role: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const SESSION_KEY = 'gridwise_session';

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    authenticate();
  }, []);

  async function authenticate() {
    try {
      // Try cached session first
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) {
        const { token, user } = JSON.parse(cached);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setState({ user, loading: false, error: null });
        return;
      }

      // Get Teams SSO token
      let teamsToken: string;
      try {
        teamsToken = await getTeamsSsoToken();
      } catch {
        // Dev mode: skip auth
        const devUser: User = {
          microsoftUserId: 'dev-user',
          microsoftUpn: 'dev@gridwise.fr',
          displayName: 'Dev User',
          boondUserId: 'dev-boond-user',
          role: 'admin',
        };
        setState({ user: devUser, loading: false, error: null });
        return;
      }

      // Exchange with backend
      const response = await apiClient.post<{
        success: boolean;
        data: { sessionToken: string; user: User };
      }>('/auth/token', { teamsToken });

      const { sessionToken, user } = response.data.data;

      // Cache session
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ token: sessionToken, user }));
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;

      setState({ user, loading: false, error: null });
    } catch (err: any) {
      setState({
        user: null,
        loading: false,
        error: err?.response?.data?.message || err?.message || 'Erreur d\'authentification',
      });
    }
  }

  return state;
}

export function clearAuth() {
  sessionStorage.removeItem(SESSION_KEY);
  delete apiClient.defaults.headers.common['Authorization'];
}
